import { useMemo } from 'react';
import { bindActionCreators } from 'redux';

import { useDispatch, useSelector } from '../context';
import type { SettingsKey, SettingsScope } from '../slices/settings/settings-slice';
import * as Sidebar from '../slices/sidebar-slice';

export function useSettingsSidebar() {
  const dispatch = useDispatch();
  const settingsSidebar = useSelector((state) => state.sidebar.settingsSidebar);

  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          closeSettingsSidebar: () => Sidebar.closeSettingsSidebar(),
          toggleSettingsSidebar: (highlightedSettingId?: SettingsKey<SettingsScope>) =>
            Sidebar.toggleSettingsSidebar({
              highlightedSettingId,
            }),
          clearHighlighted: () => Sidebar.clearHighlighted(),
        },
        dispatch,
      ),
    [dispatch],
  );

  return {
    isSettingsSidebarOpen: settingsSidebar.isOpen,
    highlightedSettingId: settingsSidebar.highlightedSettingId,
    toggleSettingsSidebar: actions.toggleSettingsSidebar,
    closeSettingsSidebar: actions.closeSettingsSidebar,
    clearHighlighted: actions.clearHighlighted,
  };
}

/**
 * TODO: This hook only needs to exist for as long as workspace exists.
 * Once workspace is removed, this hook can be replaced with the `useSettingsSidebar` hook.
 * The issue lies in how apps are mounted inside workspace.
 * Workspace has its own settings sidebar that uses the sidebar slice.
 * Since moving Query to use the same slice, opening the settings sidebar in workspace also
 * when inside the Query tool, also opens the Query settings sidebar.
 */
export function useScopedSettingsSidebar(scope?: SettingsScope) {
  const dispatch = useDispatch();
  const settingsSidebar = useSelector((state) => state.sidebar.settingsSidebar);

  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          closeSettingsSidebar: () => Sidebar.closeSettingsSidebar(),
          toggleSettingsSidebar: (highlightedSettingId?: SettingsKey<SettingsScope>) =>
            Sidebar.toggleSettingsSidebar({ scope, highlightedSettingId }),
          clearHighlighted: () => Sidebar.clearHighlighted(),
        },
        dispatch,
      ),
    [dispatch, scope],
  );

  return {
    isSettingsSidebarOpen: settingsSidebar.isOpen && settingsSidebar.scope === scope,
    highlightedSettingId: settingsSidebar.highlightedSettingId,
    toggleSettingsSidebar: actions.toggleSettingsSidebar,
    closeSettingsSidebar: actions.closeSettingsSidebar,
    clearHighlighted: actions.clearHighlighted,
  };
}
